import type { Ref } from 'vue'
import {
  useSearchSuggestions,
  type UseSearchSuggestionOptions,
  type SearchSuggestions,
  type Suggestion,
} from '../useSearchSuggestions/useSearchSuggestions'
import { useSearchboxUrlNavigation } from '../useSearchboxUrlNavigation/useSearchboxUrlNavigation'
import { isValidUrl } from '../../../utils/url/isValidUrl'

interface UseEnhancedSearchboxOptions extends UseSearchSuggestionOptions {
  enableSearchboxUrlNavigation: Ref<boolean>
}

interface UseEnhancedSearchbox {
  query: Ref<string | undefined>
  suggestions: Ref<SearchSuggestions>
  handleEnterKey: (searchQuery?: string) => Promise<void>
  handleSearchButton: () => Promise<void>
  handleSuggestionClick: (suggestion: Suggestion) => Promise<void>
  openSearchSuggestionLink: (suggestion: Suggestion) => Promise<void>
}

/**
 * Enhanced searchbox composable that combines search suggestions and URL navigation
 *
 * @param options - Configuration options including URL navigation variant
 * @returns Enhanced searchbox functions and state
 */
export const useEnhancedSearchbox = (
  options: UseEnhancedSearchboxOptions
): UseEnhancedSearchbox => {
  const { enableSearchboxUrlNavigation, ...searchOptions } = options

  // Get search suggestions functionality
  const {
    query,
    suggestions: rawSuggestions,
    openSearchSuggestionLink,
    openSearchLink,
  } = useSearchSuggestions(searchOptions)

  // Enhanced suggestions that include dual entries for valid URLs
  const suggestions = computed<SearchSuggestions>(() => {
    if (!enableSearchboxUrlNavigation.value || !query.value) {
      return rawSuggestions.value
    }

    const enhancedSuggestions: SearchSuggestions = []
    const processedTexts = new Set<string>()

    /**
     * Helper to check if a URL is complete enough to navigate to
     * Requires a valid TLD or explicit protocol
     */
    const isNavigableUrl = (text: string): boolean => {
      if (!isValidUrl(text)) return false

      // Has explicit protocol - definitely navigable
      if (/^(https?|ftp|file):\/\//i.test(text)) return true

      // Has a common TLD or multiple dots (subdomain.domain.tld)
      const hasValidTLD =
        /\.(com|org|net|edu|gov|mil|co|io|ai|dev|app|tech|info|biz|me|tv|xyz|online|site|uk|ca|au|de|fr|jp|cn|br|vn|in|ru|es|mx|it|kr|nl)(:\d+)?(\/|$)/i.test(
          text
        )
      const hasMultipleDots = (text.match(/\./g) || []).length >= 2

      return hasValidTLD || hasMultipleDots
    }

    // First, check if the query itself is a valid URL and add it as the first suggestion
    const queryIsUrl = isNavigableUrl(query.value)
    if (queryIsUrl) {
      enhancedSuggestions.push({
        text: query.value,
        icon: 'external-link',
        action: 'navigate-url',
      })
      enhancedSuggestions.push({
        text: query.value,
        icon: 'search',
        action: 'search',
      })
      processedTexts.add(query.value)
    }

    // Then process the suggestions from Yahoo API
    for (const suggestion of rawSuggestions.value) {
      // Skip if we already added this text (from the query)
      if (processedTexts.has(suggestion.text)) {
        continue
      }

      const isUrl = isNavigableUrl(suggestion.text)

      if (isUrl) {
        // Add URL navigation suggestion first (with external link icon)
        enhancedSuggestions.push({
          ...suggestion,
          icon: 'external-link',
          action: 'navigate-url',
        })

        // Add search suggestion second (with search icon)
        enhancedSuggestions.push({
          ...suggestion,
          icon: 'search',
          action: 'search',
        })

        processedTexts.add(suggestion.text)
      } else {
        // Regular search suggestion
        enhancedSuggestions.push({
          ...suggestion,
          icon: 'search',
          action: 'search',
        })
      }
    }

    return enhancedSuggestions
  })

  // Get URL navigation functionality
  const { handleSearchInput, handleSuggestionClick: handleUrlNavSuggestion } =
    useSearchboxUrlNavigation({
      enabled: enableSearchboxUrlNavigation,
    })

  async function handleEnterKey(searchQuery?: string): Promise<void> {
    const currentQuery = searchQuery ?? query.value
    if (!currentQuery) return

    // If feature flag is OFF, use original search behavior
    if (!enableSearchboxUrlNavigation.value) {
      await openSearchLink(currentQuery, 'keyboard-return')
      return
    }

    // Feature flag is ON, use new URL navigation behavior
    await handleSearchInput(currentQuery, 'keyboard-return')
  }

  async function handleSearchButton(): Promise<void> {
    const currentQuery = query.value
    if (!currentQuery) return

    // If feature flag is OFF, use original search behavior
    if (!enableSearchboxUrlNavigation.value) {
      await openSearchLink(currentQuery, 'search-button')
      return
    }

    // Feature flag is ON, use new URL navigation behavior
    await handleSearchInput(currentQuery, 'search-button')
  }

  async function handleSuggestionClick(suggestion: Suggestion): Promise<void> {
    // If feature flag is OFF, use original search behavior
    if (!enableSearchboxUrlNavigation.value) {
      await openSearchSuggestionLink(suggestion)
      return
    }

    // Feature flag is ON, use new URL navigation behavior
    await handleUrlNavSuggestion(suggestion)
  }

  return {
    query,
    suggestions,
    handleEnterKey,
    handleSearchButton,
    handleSuggestionClick,
    openSearchSuggestionLink,
  }
}
